import {observable, ObservableMap, action, makeObservable, observe} from 'mobx';
import localforage from "localforage";

const ONE_MINUTE = 1000 * 60;
const FIVE_MINUTES = ONE_MINUTE * 5;
export const DATA_INTERVAL = FIVE_MINUTES;

export class DataStore {

    myProjectsMap = new ObservableMap();
    isLoadingProjects = false;
    projectsFoldersMap = new ObservableMap();
    projectsFoldersExpirationsMap = new ObservableMap();
    projectsProceduresMap = new ObservableMap();
    projectsProceduresExpirationMap = new ObservableMap();
    folderContentMap = new ObservableMap();
    notifications = new ObservableMap();
    playerSettings = {};
    projectsDataExpiration = 0;
    notificationsDataExpiration = 0;
    playerSettingsDataExpiration = 0;

    deleteAll() {
        localforage.clear().then();
        this.myProjectsMap.clear();
        this.projectsFoldersMap.clear();
        this.projectsFoldersExpirationsMap.clear();
        this.projectsProceduresMap.clear();
        this.projectsProceduresExpirationMap.clear();
        this.folderContentMap.clear();
        this.notifications.clear();
        this.playerSettings = {};

        this.projectsDataExpiration = 0;
        this.playerSettingsDataExpiration = 0;
        this.notificationsDataExpiration = 0;
    }

    removeSelfFromProject = projectId => {
        this.myProjectsMap.delete(projectId);
        this.projectsFoldersMap.delete(projectId);
        this.projectsFoldersExpirationsMap.delete(projectId);
        this.projectsProceduresMap.delete(projectId);
        this.projectsProceduresExpirationMap.delete(projectId);
        for (const key of this.folderContentMap.keys()) {
            if (key.startsWith(`${projectId}:`)) this.folderContentMap.delete(key);
        }
    };

    constructor() {
        makeObservable(this, {
            myProjectsMap: observable,
            isLoadingProjects: observable,
            fetchProjectsIfEmpty: action,
            projectsFoldersMap: observable,
            projectsFoldersExpirationsMap: observable,
            projectsProceduresMap: observable,
            projectsProceduresExpirationMap: observable,
            folderContentMap: observable,
            notifications: observable,
            playerSettings: observable,
            projectsDataExpiration: observable,
            notificationsDataExpiration: observable,
            playerSettingsDataExpiration: observable,
            deleteAll: action,
            removeSelfFromProject: action
        });

        this.fetchProjectsIfEmpty();

        const now = Date.now();

        localforage.getItem('projectsDataExpiration').then(storedProjectsDataExpiration => {
            this.projectsDataExpiration = parseInt(storedProjectsDataExpiration) || 0;
            if (this.projectsDataExpiration > now) {
                localforage.getItem('myProjectsMap').then(item => {
                    const storedMyProjects = JSON.parse(item);
                    if (!!storedMyProjects) {
                        const newProjectsMap = new ObservableMap();
                        for (const projectPair of Object.entries(storedMyProjects)) {
                            newProjectsMap.set(projectPair[0], projectPair[1]);
                        }
                        this.myProjectsMap = newProjectsMap;
                    }
                });

            }
        });

        this.getMapFromLocalStorage('projectsFoldersMap');
        this.getMapFromLocalStorage('projectsFoldersExpirationsMap');
        this.getMapFromLocalStorage('projectsProceduresMap');
        this.getMapFromLocalStorage('projectsProceduresExpirationMap');

        localforage.getItem('playerSettingsDataExpiration').then(playerSettingsDataExpiration => {
            this.playerSettingsDataExpiration = parseInt(playerSettingsDataExpiration) || 0;
            if (this.playerSettingsDataExpiration > now) {
                localforage.getItem('playerSettings').then(item => {
                    const storedPlayerSettings = JSON.parse(item);
                    if (!!storedPlayerSettings) {
                        this.playerSettings = storedPlayerSettings;
                    }
                });
            }
        });

        localforage.getItem('notificationsDataExpiration').then(notificationsDataExpiration => {
            this.notificationsDataExpiration = parseInt(notificationsDataExpiration) || 0;
            if (this.notificationsDataExpiration > now) {
                localforage.getItem('notifications').then(item => {
                    const storedNotifications = JSON.parse(item);
                    if (!!storedNotifications) {
                        this.notifications = new Map(Object.entries(storedNotifications));
                    }
                });
            }
        });

        this.registerWriteVariableToStorage('projectsDataExpiration');
        this.registerWriteVariableToStorage('notificationsDataExpiration');
        this.registerWriteVariableToStorage('playerSettingsDataExpiration');
        this.registerWriteVariableToStorage('playerSettings'); // TODO works for object?
    }

    fetchProjectsIfEmpty() {
        if (this.myProjectsMap.size === 0) {
            this.isLoadingProjects = true;

            localforage.getItem('myProjectsMap').then(item => {
                const storedMyProjects = JSON.parse(item);
                if (!!storedMyProjects) {
                    const newProjectsMap = new ObservableMap();
                    for (const projectPair of Object.entries(storedMyProjects)) {
                        newProjectsMap.set(projectPair[0], projectPair[1]);
                    }
                    this.myProjectsMap = newProjectsMap;
                }
            }).finally(() => {
                this.isLoadingProjects = false;
            });
        } else {
            this.isLoadingProjects = false;
        }
    }

    registerWriteVariableToStorage(dataKey) {
        observe(this, dataKey, change => {
            localforage.setItem(dataKey, JSON.stringify(change.newValue))
                .then()
                .catch(e => console.error('could not store in localstorage', e));
        });
    }

    set(mapName, mapKey, mapValue) {
        this[mapName].set(mapKey, mapValue);
        const objectified = this.objectifyMap(this[mapName]);
        localforage.setItem(mapName, JSON.stringify(objectified))
            .then()
            .catch(e => console.error('could not store in localstorage', e));
    }

    objectifyMap(map) {
        const objectified = {};
        map.forEach((value, key) => {
            if (value instanceof ObservableMap) {
                objectified[key] = Object.fromEntries(value);
            } else {
                objectified[key] = value;
            }
        });
        return objectified;
    }

    setMapInStorage(cacheKey, map) {
        const objectifiedMap = this.objectifyMap(map)
        localforage.setItem(cacheKey, JSON.stringify(objectifiedMap))
            .then(() => this.loadMapFromStorage(cacheKey))
            .catch(e => console.error('could not store in localstorage', e));
    }

    loadMapFromStorage(cacheKey) {
        localforage.getItem(cacheKey).then(item => {
            const storedMap = JSON.parse(item);
            if (!!storedMap) {
                const entries = Object.entries(storedMap);
                const newMap = new ObservableMap();
                entries.forEach(entry => {
                    const projectId = entry[0];
                    const value = entry[1];
                    newMap.set(projectId, value);
                });
                this[cacheKey] = newMap;
            }
        })
    }

    getMapFromLocalStorage(cacheKey) {
        localforage.getItem(cacheKey).then(item => {
            const storedMap = JSON.parse(item);
            if (!!storedMap) {
                const entries = Object.entries(storedMap);
                this[cacheKey].clear();
                entries.forEach(entry => {
                    const projectId = entry[0];
                    const value = entry[1];
                    if (!isNaN(value)) {
                        this[cacheKey].set(projectId, value);
                    } else {
                        const innerMap = new Map(Object.entries(value));
                        this[cacheKey].set(projectId, innerMap);
                    }
                });
            }
        })
    }
}
